import { AttachmentItem } from "../../../../models/shared/AttachmentList";
import { AttachmentSource, AttachmentVisual, BackButton, FoldersHierarchy } from "./AttachmentListComponentTypes";

export const PATH_SEPARATOR = "\\";

export const ROOT_FOLDER_KEY = "bPddtp9TtGMcububNeUDk8g7fTZIPrjY.02cd99e9-8394-4406-b988-07b074365975";

export const MAX_ATTACHMENTS = 25;

/** Extract file extension from a full filename (e.g. "report.pdf" → "pdf"). */
export function getIconExtension(attachmentFullName: string): string {
    const extensionIndex = attachmentFullName.lastIndexOf(".");
    return extensionIndex > -1 ? attachmentFullName.substring(extensionIndex + 1) : "";
}

/** Strip the leading path separator if present. */
export function normalizeAttachmentId(id: string): string {
    return id.startsWith(PATH_SEPARATOR) ? id.slice(1) : id;
}

/** Check if two paths refer to the same file (normalized comparison). */
export function equalFiles(left: string, right: string): boolean {
    return normalizeAttachmentId(left) === normalizeAttachmentId(right);
}

/** Check if the `left` path is inside the `right` folder. */
export function equalFolders(left: string, right: string): boolean {
    return normalizeAttachmentId(left).startsWith(normalizeAttachmentId(right) + PATH_SEPARATOR);
}

/** Sort comparator: folders first, then alphabetical within same type. */
export function sortAttachmentVisuals(left: AttachmentVisual, right: AttachmentVisual): number {
    const nameA = left.name.toLowerCase();
    const nameB = right.name.toLowerCase();
    if (left.type === right.type) {
        return nameA < nameB ? -1 : 1;
    }
    return left.type === "folder" ? -1 : 1;
}

/**
 * Returns the path used for folder hierarchy rendering.
 * Project-folder items carry a `displayPath` field (their NRN-based `id` is
 * decoupled from display).  All other items fall back to `id`.
 */
export function getAttachmentDisplayPath(item: AttachmentItem): string {
    const withPath = item as {
        displayPath?: string;
        addToParentFolder?: boolean;
        projectFolderRelativePath?: string;
        name?: string;
    };
    if (withPath.addToParentFolder === false) {
        return `${withPath.projectFolderRelativePath}\\${withPath.name}`;
    }

    return withPath.displayPath ?? item.id;
}

/** Build the folder hierarchy tree from a flat list of attachment items. */
export function prepareFoldersStructure(newItems: AttachmentItem[]): FoldersHierarchy {
    const foldersOnlyAttachments = newItems.filter((item) => getAttachmentDisplayPath(item).includes(PATH_SEPARATOR));

    const hierarchy: FoldersHierarchy = { children: [], name: "", path: "" };
    foldersOnlyAttachments.forEach((item) => {
        const source = isProjectAttachment(item) ? "project" : "local";
        getAttachmentDisplayPath(item)
            .split(PATH_SEPARATOR)
            .filter((fileOrFolderName) => fileOrFolderName)
            .reduce((h, fileOrFolderName) => {
                const existingObj = h.children.find((i) => i.name === fileOrFolderName);
                if (existingObj) {
                    return existingObj;
                }
                const newObj: FoldersHierarchy = {
                    children: [],
                    name: fileOrFolderName,
                    path: `${h.path}${PATH_SEPARATOR}${fileOrFolderName}`,
                    source,
                };
                h.children.push(newObj);
                return newObj;
            }, hierarchy);
    });

    const filesOnlyAttachments: FoldersHierarchy[] = newItems
        .filter((item) => !getAttachmentDisplayPath(item).includes(PATH_SEPARATOR))
        .map((item) => {
            const source: AttachmentSource = isProjectAttachment(item) ? "project" : "local";
            return {
                children: [] as FoldersHierarchy[],
                path: getAttachmentDisplayPath(item),
                name: item.name,
                source,
            };
        });

    return {
        name: "root",
        path: ROOT_FOLDER_KEY,
        children: hierarchy.children.concat(filesOnlyAttachments),
    };
}

/** Recursively search the hierarchy for a node matching the given key. Returns the node with its parent. */
export function findInHierarchy(
    hierarchy: FoldersHierarchy,
    key: string
): (FoldersHierarchy & { parent?: FoldersHierarchy }) | undefined {
    if (hierarchy.path.toLowerCase() === key.toLowerCase()) {
        return hierarchy;
    }
    if (hierarchy.children.length === 0) {
        return undefined;
    }
    const foundItem = hierarchy.children.find((item) => item.path.toLowerCase() === key.toLowerCase());
    if (foundItem) {
        return {
            ...foundItem,
            parent: hierarchy,
        };
    }
    for (const item of hierarchy.children) {
        const foundItemInChildren = findInHierarchy(item, key);
        if (foundItemInChildren) {
            return foundItemInChildren;
        }
    }
    return undefined;
}

/** Create visual data from hierarchy nodes. Stores extension string instead of JSX. */
export function isProjectAttachment(item: AttachmentItem): boolean {
    const withProjectMeta = item as {
        isFromProjectFolder?: boolean;
        projectId?: string;
        apiPath?: string;
        displayPath?: string;
        rootFolderNrn?: string;
        projectFolderRelativePath?: string;
    };

    return Boolean(
        withProjectMeta.isFromProjectFolder ||
            withProjectMeta.projectId ||
            withProjectMeta.apiPath ||
            withProjectMeta.displayPath ||
            withProjectMeta.rootFolderNrn ||
            withProjectMeta.projectFolderRelativePath
    );
}

export function createAttachmentVisuals(hierarchy: FoldersHierarchy[]): AttachmentVisual[] {
    return hierarchy.map((item) => {
        if (item.children.length === 0) {
            return {
                extension: getIconExtension(item.name),
                name: item.name,
                key: item.path,
                type: "file" as const,
                source: item.source,
            };
        }
        return {
            name: item.name,
            key: item.path,
            type: "folder" as const,
            source: item.source,
        };
    });
}

/** Factory for BackButton objects. */
export function createBackButton(folderName: string, folderKey: string, parentKey: string): BackButton {
    return { folderName, folderKey, parentKey };
}

/**
 * After an item is removed, resolve which back button should be shown.
 * Walks up the hierarchy until a valid folder is found, or returns undefined (root).
 */
export function resolveBackButton(
    currentBack: BackButton | undefined,
    attachments: AttachmentItem[]
): BackButton | undefined {
    if (!currentBack) {
        return undefined;
    }
    // If the current folder still has children, keep the back button as-is.
    if (attachments.find((attachment) => equalFolders(getAttachmentDisplayPath(attachment), currentBack.folderKey))) {
        return currentBack;
    }
    // Walk up the tree to find a valid parent.
    return findValidParentBackButton(currentBack.folderKey, attachments);
}

function findValidParentBackButton(
    folderKey: string | undefined,
    attachments: AttachmentItem[]
): BackButton | undefined {
    if (!folderKey) {
        return undefined;
    }
    const hierarchy = prepareFoldersStructure(attachments);
    const hierarchyItem = findInHierarchy(hierarchy, folderKey);
    if (hierarchyItem?.parent) {
        return createBackButton(hierarchyItem.name, hierarchyItem.path, hierarchyItem.parent.path);
    }
    const lastSepIndex = folderKey.lastIndexOf(PATH_SEPARATOR);
    if (lastSepIndex === -1) {
        return undefined;
    }
    const parentFolderPath = folderKey.slice(0, lastSepIndex);
    return findValidParentBackButton(parentFolderPath, attachments);
}

/** Get the AttachmentItems to remove when a visual item is dismissed. */
export function getItemsToRemove(attachment: AttachmentVisual, allItems: AttachmentItem[]): AttachmentItem[] {
    switch (attachment.type) {
        case "file":
            return allItems.filter((att) => equalFiles(getAttachmentDisplayPath(att), attachment.key));
        case "folder":
            return allItems.filter((att) => equalFolders(getAttachmentDisplayPath(att), attachment.key));
    }
}
